import { Stack, styled } from '@mui/material';
import * as React from 'react';
import { communityContent } from '../../community-content';
import MomentsCommunityImage from './components/moments-community-image/moments-community-image';

import Image from 'next/image';
import { ReactNode } from 'react';
import { HomepageContent } from '../../../../lib/fetch-content';
import { MomentsModal } from '../moments-modal/moments-modal';
import DotActiveImg from '../../../header-carousel/assets/buttons/dot-active.png';
import DotDefaultImg from '../../../header-carousel/assets/buttons/dot.png';
import LeftHoverImg from '../../../header-carousel/assets/buttons/left-hover.png';
import LeftImg from '../../../header-carousel/assets/buttons/left.png';
import RightHoverImg from '../../../header-carousel/assets/buttons/right-hover.png';
import RightImg from '../../../header-carousel/assets/buttons/right.png';
import MomentsSteps from './components/moments-steps/moments-steps';

const OuterStack = styled(Stack)`
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    justify-content: center;
    margin-left: 0px !important;
  }
`;

const InnerStack = styled(Stack)`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  position: relative; /* Required for positioning the pseudo-elements */
  border-width: 3px 0 3px 0; /* Top and bottom borders */
  border-style: solid;
  border-image: linear-gradient(
      270deg,
      rgba(198, 153, 187, 0) 0%,
      rgba(198, 153, 187, 0.5) 50%,
      rgba(198, 153, 187, 0) 100%
    )
    1;
  padding: 20px 0; /* Adds space between borders and content */
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
    flex-direction: column;
    justify-content: center;
    margin-left: 0px !important;
  }
`;

const TopGradient = styled('div')`
  height: 100px;
  margin-top: -50px;
  width: 100%;
  background: radial-gradient(
    50% 50% at 50.05% 100%,
    rgba(198, 153, 187, 0.15) 0%,
    rgba(198, 153, 187, 0) 100%
  );
  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 50px;
    margin-top: -25px;
    background: radial-gradient(
      50% 50% at 50.05% 100%,
      rgba(198, 153, 187, 0.15) 0%,
      rgba(198, 153, 187, 0) 100%
    );
  }
`;

const BottomGradient = styled('div')`
  height: 100px;
  margin-bottom: -50px;
  width: 100%;
  background: radial-gradient(
    50% 50% at 50% 0%,
    rgba(198, 153, 187, 0.15) 0%,
    rgba(198, 153, 187, 0) 100%
  );
  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 50px;
    margin-bottom: -25px;
    background: radial-gradient(
      50% 50% at 50% 0%,
      rgba(198, 153, 187, 0.15) 0%,
      rgba(198, 153, 187, 0) 100%
    );
  }
`;
const ImageStack = styled(Stack)`
  width: 60%;
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const TextStack = styled(Stack)`
  width: 40%;
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const Controls = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 500px;
  align-self: center;
  padding: 20px 0;
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const ArrowButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 10px;
  position: relative;
  display: flex;

  .arrow-default {
    transition: opacity 0.3s ease-in-out;
  }

  .arrow-hover {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover .arrow-default {
    opacity: 0;
  }

  &:hover .arrow-hover {
    opacity: 1;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 0 5px;
    img {
      width: 40px !important;
      height: 40px !important;
    }
  }
`;

const Dots = styled('div')`
  display: flex;
  align-items: center;
`;

const Dot = styled('div')<{ active: boolean }>`
  width: ${props => (props.active ? '24px' : '12px')};
  height: ${props => (props.active ? '24px' : '12px')};
  margin: 0 5px;
  background: ${props =>
    props.active ? `url('${DotActiveImg.src}')` : `url('${DotDefaultImg.src}')`};
  background-size: cover;
  scale: ${props => (props.active ? 1.5 : 1)};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: url('${DotActiveImg.src}');
    background-size: cover;
    scale: ${props => (props.active ? 1.5 : 2.5)};
  }
`;

export interface Slide {
  title: string;
  host: string;
  hostDescription: string;
  profileImage: string;
  mainImage: string;
  shortDescription: ReactNode;
  longDescription: ReactNode;
}

interface MomentsCommunityStepperProps {
  homepageContent: HomepageContent;
  slides?: Slide[];
}

export function MomentsCommunityStepper({
  slides = [],
  homepageContent,
}: MomentsCommunityStepperProps) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isFading, setIsFading] = React.useState(false);
  const maxStep = slides.length - 1;

  const fadeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (fadeTimer.current) {
        clearTimeout(fadeTimer.current);
      }
    };
  }, []);

  const activeSlide = slides[activeStep];

  const handleNext = () => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(prev => (prev === maxStep ? 0 : prev + 1));
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  const handleBack = () => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(prev => (prev === 0 ? maxStep : prev - 1));
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  const handleDotClick = (index: number) => {
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setActiveStep(index);
      fadeTimer.current = setTimeout(() => {
        setIsFading(false);
      }, 100);
    }, 1000);
  };

  if (!activeSlide) {
    return null;
  }

  return (
    <>
      <MomentsModal homepageContent={homepageContent} />
      <OuterStack>
        <TopGradient />
        <InnerStack
          sx={{
            transition: 'opacity 1s',
            opacity: isFading ? 0 : 1,
            gap: '40px !important',
          }}
        >
          <ImageStack>
            <MomentsCommunityImage src={activeSlide.mainImage} alt={activeSlide.title} />
          </ImageStack>
          <TextStack>
            <MomentsSteps
              host={activeSlide.host}
              hostDescription={activeSlide.hostDescription}
              profileImageProps={{
                src: activeSlide.profileImage,
                alt: activeSlide.host,
              }}
              shortDescription={activeSlide.shortDescription}
              index={activeStep}
            />
          </TextStack>
        </InnerStack>
        <BottomGradient />
        <Controls>
          <ArrowButton onClick={handleBack}>
            <div className="arrow-default">
              <Image width={64} height={64} src={LeftImg} alt="Previous" />
            </div>
            <div className="arrow-hover">
              <Image width={64} height={64} src={LeftHoverImg} alt="Previous" />
            </div>
          </ArrowButton>
          <Dots>
            {slides.map((_, index) => (
              <Dot
                key={index}
                active={index === activeStep}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </Dots>
          <ArrowButton onClick={handleNext}>
            <div className="arrow-default">
              <Image width={64} height={64} src={RightImg} alt="Next" />
            </div>
            <div className="arrow-hover">
              <Image width={64} height={64} src={RightHoverImg} alt="Next" />
            </div>
          </ArrowButton>
        </Controls>
      </OuterStack>
    </>
  );
}
